import type { CalcInputs } from "@/types/calc";
import {
  CALC_LIMITS, hardLimitError, htfRatedMin, fmtLimitNum,
  MIN_PRODUCT_DT_K, MIN_APPROACH_K, FREEZE_POINT_MARGIN_K,
} from "@/lib/limits";

// Default form values used until the backend defaults load
export const FALLBACK_INPUTS: CalcInputs = {
  productName: "MDM Poultry",
  thermalProperty: "Poultry",
  inletTemp: "10",
  outletTemp: "-18",
  density: "890",
  contactPc: "100",
  refrigerantType: "Primary",
  refrigerant: "R717 - Ammonia",
  evapTemp: "-40",
  heatTransferFluid: "Temper -40",
  htfInletTemp: "-40",
  htfDeltaT: "3",
  nFreezers: "4",
  nStations: "36",
  lStation: "810",
  dStation: "600",
  tStation: "76",
  nDividers: "1",
  loadingTime: "15",
  unloadingTime: "15",
  waitMisc: "5",
  cyclesWindowHrs: "16",
  overrideFreezeTime: "",
  overrideDefrostMin: "",
};

// Allowed values for the enum-like Calculator field
export const REFRIGERANT_TYPES = ["Primary", "Secondary"] as const;

/**
 * Coerce an untrusted object (localStorage, a hand-edited file) into a
 * partial CalcInputs: unknown keys are dropped, numbers / booleans become
 * strings, anything else (objects, arrays, null) is ignored, and an unknown
 * refrigerantType is dropped so the default applies. Never throws, so a
 * corrupt entry can't crash .trim() / .includes() further down.
 */
export function coerceCalcInputs(raw: unknown): Partial<CalcInputs> {
  const out: Partial<CalcInputs> = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  const src = raw as Record<string, unknown>;
  (Object.keys(FALLBACK_INPUTS) as (keyof CalcInputs)[]).forEach((k) => {
    const v = src[k];
    if (typeof v === "string") out[k] = v;
    else if ((typeof v === "number" && isFinite(v)) || typeof v === "boolean") out[k] = String(v);
  });
  if (out.refrigerantType !== undefined
      && !(REFRIGERANT_TYPES as readonly string[]).includes(out.refrigerantType)) {
    delete out.refrigerantType;
  }
  return out;
}

// Numeric fields common to both refrigerant types
export const NUMERIC_FIELDS: (keyof CalcInputs)[] = [
  "inletTemp", "outletTemp", "density", "contactPc",
  "nFreezers", "nStations", "lStation", "dStation", "tStation",
  "nDividers", "loadingTime", "unloadingTime", "waitMisc", "cyclesWindowHrs",
];

// MAN overrides — blank = AUTO, otherwise checked like any numeric field
const OVERRIDE_FIELDS: (keyof CalcInputs)[] = ["overrideFreezeTime", "overrideDefrostMin"];

function isBlankOrNaN(v: string): boolean {
  // Strip the override marker (!) before parsing so "75!" is treated as valid
  const cleaned = (v || "").replace(/!/g, "").trim();
  return cleaned === "" || Number.isNaN(Number(cleaned));
}

/**
 * Message for a blank / non-numeric entry. A decimal comma ("10,5") gets a
 * specific hint instead of the generic "Enter a number".
 */
export function notANumberMessage(v: string): string {
  const cleaned = (v || "").replace(/!/g, "").trim();
  if (/^-?\d*,\d+$/.test(cleaned)) {
    return `Use a point, not a comma (${cleaned.replace(",", ".")})`;
  }
  return "Enter a number";
}

/** Numeric value with the override marker (!) stripped; NaN when blank. */
function num(v: string): number {
  const cleaned = (v || "").replace(/!/g, "").trim();
  return cleaned === "" ? NaN : Number(cleaned);
}

// ── Stack-closed constraint ───────────────────────────────────────────────
// nPlates = nStations + 1
// tPlate = 19 mm
// lStackClosed = tPlate + (nStations * (tPlate + minOpening))
// limitStackClosed = 4620 mm
const T_PLATE_MM = 19;
const LIMIT_STACK_CLOSED_MM = 4620;

/**
 * Mirrors the backend _t_station / _min_opening logic so we can compute the
 * effective plate spacing before the blur autocorrect has fired.
 */
function minOpeningMM(tStation: string): number {
  const n = Math.round(Number((tStation || "").replace(/!/g, "").trim()));
  if (n === 75) return 76;
  if (n === 100 || n === 101) return 102;
  return n;
}

export interface LocalErrorContext {
  /** Freezing point (°C) of the selected product, from the /products data. */
  productTFus?: number | null;
}

/**
 * Returns a map of field -> error message for client-side issues: blank/NaN,
 * hard limits (lib/limits — the ! override never bypasses these) and the
 * cross-field rules mirrored from the backend validate(). Any entry blocks the
 * calculation.
 */
export function localErrors(inputs: CalcInputs, ctx: LocalErrorContext = {}): Record<string, string> {
  const errs: Record<string, string> = {};
  const isSecondary = inputs.refrigerantType === "Secondary";

  /** Blank/NaN → "Enter a number", otherwise the hard-limit check. */
  const check = (field: keyof CalcInputs) => {
    if (isBlankOrNaN(inputs[field])) { errs[field] = notANumberMessage(inputs[field]); return; }
    const hard = hardLimitError(CALC_LIMITS[field], inputs[field]);
    if (hard) errs[field] = hard;
  };

  for (const field of NUMERIC_FIELDS) check(field);

  if (!isSecondary) {
    check("evapTemp");
  } else {
    check("htfInletTemp");
    check("htfDeltaT");
  }

  // Overrides: blank = AUTO (no error); anything else must be a valid number
  for (const field of OVERRIDE_FIELDS) {
    if ((inputs[field] || "").trim() !== "") check(field);
  }

  // ── Cross-field rules (mirror backend validate()) ───────────────────────
  const inlet = num(inputs.inletTemp);
  const outlet = num(inputs.outletTemp);
  const outletOk = !errs.outletTemp;

  // Inlet must be at least 10 K above outlet
  if (!errs.inletTemp && outletOk && inlet - outlet < MIN_PRODUCT_DT_K) {
    errs.inletTemp = `Must be ≥ ${fmtLimitNum(outlet + MIN_PRODUCT_DT_K)} °C (${MIN_PRODUCT_DT_K} K above outlet)`;
  }

  // Outlet at least 1 K below the product's freezing point (when known)
  const tFus = ctx.productTFus;
  if (outletOk && tFus != null && isFinite(tFus) && outlet > tFus - FREEZE_POINT_MARGIN_K) {
    errs.outletTemp = `Must be ≤ ${fmtLimitNum(tFus - FREEZE_POINT_MARGIN_K)} °C (${FREEZE_POINT_MARGIN_K} K below freezing point)`;
  }

  if (!isSecondary) {
    // Evaporation at least 10 K below outlet
    const evap = num(inputs.evapTemp);
    if (!errs.evapTemp && !errs.outletTemp && evap > outlet - MIN_APPROACH_K) {
      errs.evapTemp = `Must be ≤ ${fmtLimitNum(outlet - MIN_APPROACH_K)} °C (${MIN_APPROACH_K} K below outlet)`;
    }
  } else {
    const htfIn = num(inputs.htfInletTemp);
    const htfDt = num(inputs.htfDeltaT);
    // Not below the fluid's rated minimum (number in its label)
    const ratedMin = htfRatedMin(inputs.heatTransferFluid);
    if (!errs.htfInletTemp && ratedMin != null && htfIn < ratedMin) {
      errs.htfInletTemp = `Must be ≥ ${fmtLimitNum(ratedMin)} °C (${inputs.heatTransferFluid} rated minimum)`;
    }
    // Fluid outlet (inlet + ΔT) at least 10 K below product outlet
    if (!errs.htfInletTemp && !errs.htfDeltaT && !errs.outletTemp
        && htfIn + htfDt > outlet - MIN_APPROACH_K) {
      errs.htfInletTemp = `Inlet + ΔT must be ≤ ${fmtLimitNum(outlet - MIN_APPROACH_K)} °C (${MIN_APPROACH_K} K below outlet)`;
    }
  }

  // Plate stack closed-length constraint — highlight both fields that can fix it
  if (!errs.nStations && !errs.tStation) {
    const nSt = num(inputs.nStations);
    const minOp = minOpeningMM(inputs.tStation);
    if (nSt > 0 && minOp > 0) {
      const lStack = T_PLATE_MM + (nSt * (T_PLATE_MM + minOp));
      if (lStack > LIMIT_STACK_CLOSED_MM) {
        const msg = `Stack ${Math.round(lStack)} mm > ${LIMIT_STACK_CLOSED_MM} mm — reduce stations or thickness`;
        errs.nStations = msg;
        errs.tStation = msg;
      }
    }
  }

  return errs;
}
